/**
 * Moving sections around a report.
 *
 * Every function here takes a tree and returns a new one. Nothing is mutated
 * in place, which buys two things worth more than the copying costs: undo is
 * just keeping the previous tree, and Vue sees a changed reference rather than
 * having to track mutation through nested arrays.
 *
 * The operation that has to be right is `move`. Drag and drop lets somebody
 * try to drop a group inside itself, and the result of allowing it is a cycle:
 * a tree that renders forever, an outline that never terminates, and a
 * document that cannot be saved. So `move` refuses rather than trusting the
 * interface to have prevented it, because the interface is where the mistake
 * is easiest to make.
 *
 * Pure. No framework, no store, no network.
 */

import { duplicateSection, isGroup, type Section } from '@/domain/section'

/** Where a section sits: its parent, and its index among that parent's children. */
export interface Position {
  /** Null at the top level. */
  readonly parentId: string | null
  readonly index: number
}

/** A section paired with how deep it sits, for drawing an outline. */
export interface FlatSection {
  readonly section: Section
  readonly depth: number
  readonly parentId: string | null
  readonly index: number
}

/** The section with this id, wherever it is. */
export function find(sections: readonly Section[], id: string): Section | null {
  for (const section of sections) {
    if (section.id === id) return section
    if (isGroup(section)) {
      const found = find(section.children, id)
      if (found) return found
    }
  }
  return null
}

/** Where a section sits, or null when it is not in this tree. */
export function locate(
  sections: readonly Section[],
  id: string,
  parentId: string | null = null,
): Position | null {
  for (const [index, section] of sections.entries()) {
    if (section.id === id) return { parentId, index }
    if (isGroup(section)) {
      const found = locate(section.children, id, section.id)
      if (found) return found
    }
  }
  return null
}

/** The chain of groups containing a section, outermost first. */
export function ancestors(sections: readonly Section[], id: string): readonly Section[] {
  const chain: Section[] = []

  const walk = (nodes: readonly Section[], trail: Section[]): boolean => {
    for (const node of nodes) {
      if (node.id === id) {
        chain.push(...trail)
        return true
      }
      if (isGroup(node) && walk(node.children, [...trail, node])) return true
    }
    return false
  }

  walk(sections, [])
  return chain
}

/** Whether a section holds another, at any depth. */
export function contains(section: Section, id: string): boolean {
  if (!isGroup(section)) return false
  return section.children.some((child) => child.id === id || contains(child, id))
}

/**
 * The tree as a list, skipping what is collapsed.
 *
 * What the outline draws. Collapsed groups keep their children out of the list
 * entirely, so a drop target inside a collapsed group cannot be aimed at,
 * which is the correct behaviour: you cannot drop into something you cannot
 * see.
 */
export function flatten(
  sections: readonly Section[],
  depth = 0,
  parentId: string | null = null,
): readonly FlatSection[] {
  const rows: FlatSection[] = []

  for (const [index, section] of sections.entries()) {
    rows.push({ section, depth, parentId, index })
    if (isGroup(section) && !section.collapsed) {
      rows.push(...flatten(section.children, depth + 1, section.id))
    }
  }

  return rows
}

/** Every section, whatever its depth or collapsed state. */
export function walk(sections: readonly Section[]): readonly Section[] {
  const all: Section[] = []
  for (const section of sections) {
    all.push(section)
    if (isGroup(section)) all.push(...walk(section.children))
  }
  return all
}

/** How many sections the report holds, at every depth. */
export function count(sections: readonly Section[]): number {
  return walk(sections).length
}

/** The tree without the section with this id. */
export function remove(sections: readonly Section[], id: string): Section[] {
  const kept: Section[] = []

  for (const section of sections) {
    if (section.id === id) continue
    if (isGroup(section)) {
      kept.push({ ...section, children: remove(section.children, id) })
    } else {
      kept.push(section)
    }
  }

  return kept
}

/**
 * The tree with a section put in at a given place.
 *
 * An index past the end appends, which is what a drop below the last row
 * means. A negative one prepends. Neither is an error worth refusing: the
 * caller is describing a position, not asserting one exists.
 */
export function insert(sections: readonly Section[], section: Section, at: Position): Section[] {
  if (at.parentId === null) {
    const copy = [...sections]
    copy.splice(clamp(at.index, copy.length), 0, section)
    return copy
  }

  return sections.map((node) => {
    if (node.id === at.parentId && isGroup(node)) {
      const children = [...node.children]
      children.splice(clamp(at.index, children.length), 0, section)
      return { ...node, children }
    }
    if (isGroup(node)) {
      return { ...node, children: insert(node.children, section, at) }
    }
    return node
  })
}

function clamp(index: number, length: number): number {
  if (index < 0) return 0
  if (index > length) return length
  return index
}

/** Why a move was refused, or null when it may go ahead. */
export type MoveRefusal = 'no-such-section' | 'target-is-not-a-group' | 'would-nest-inside-itself'

/**
 * Whether a section may be moved to a place.
 *
 * The check that matters is the last one. Dropping a group into its own
 * descendant detaches the whole branch from the tree and points it at itself:
 * the outline recurses forever, the document cannot be serialised, and the
 * work in that branch is gone. It is easy to do by accident with a mouse,
 * which is exactly why it is refused here rather than trusted to the interface.
 */
export function refuseMove(
  sections: readonly Section[],
  id: string,
  to: Position,
): MoveRefusal | null {
  const moving = find(sections, id)
  if (!moving) return 'no-such-section'

  if (to.parentId !== null) {
    const parent = find(sections, to.parentId)
    if (!parent) return 'no-such-section'
    if (!isGroup(parent)) return 'target-is-not-a-group'
    if (to.parentId === id || contains(moving, to.parentId)) return 'would-nest-inside-itself'
  }

  return null
}

/**
 * The tree with a section moved.
 *
 * Returns the tree unchanged when the move is refused, so a caller that does
 * not check still cannot corrupt the document.
 *
 * The index is interpreted against the tree **with the section already
 * removed**, which is what makes dragging a row down by one place work. Read
 * against the original tree, "move to index 3" of an item currently at index 2
 * puts it back where it started, and the row appears not to move at all.
 */
export function move(sections: readonly Section[], id: string, to: Position): Section[] {
  if (refuseMove(sections, id, to) !== null) return [...sections]

  const moving = find(sections, id)
  if (!moving) return [...sections]

  const from = locate(sections, id)
  const without = remove(sections, id)

  // Removing a section that sat before the target, under the same parent,
  // shifts everything after it down one.
  let index = to.index
  if (from && from.parentId === to.parentId && from.index < to.index) index -= 1

  return insert(without, moving, { parentId: to.parentId, index })
}

/** The tree with one section replaced by a changed version of itself. */
export function replace(sections: readonly Section[], updated: Section): Section[] {
  return sections.map((section) => {
    if (section.id === updated.id) return updated
    if (isGroup(section)) return { ...section, children: replace(section.children, updated) }
    return section
  })
}

/**
 * The tree with a section put directly after another.
 *
 * What duplicating does: the copy belongs next to the original, at whatever
 * depth the original sits, not at the end of the document.
 */
export function insertAfter(
  sections: readonly Section[],
  siblingId: string,
  section: Section,
): Section[] {
  const at = locate(sections, siblingId)
  if (!at) return [...sections, section]
  return insert(sections, section, { parentId: at.parentId, index: at.index + 1 })
}

/**
 * The tree with a section moved one place within its own parent.
 *
 * Keyboard reordering. Deliberately does not move between parents: a key press
 * that silently pulled a section out of its group would be a surprise, and
 * there is no way to undo it by pressing the opposite key.
 */
export function nudge(sections: readonly Section[], id: string, direction: -1 | 1): Section[] {
  const at = locate(sections, id)
  if (!at) return [...sections]

  const siblings = at.parentId === null ? sections : childrenOf(sections, at.parentId)
  const target = at.index + direction
  if (target < 0 || target >= siblings.length) return [...sections]

  // `move` reads the index against the tree with the section removed, so a
  // downward nudge needs the extra step to land past its neighbour.
  return move(sections, id, {
    parentId: at.parentId,
    index: direction === 1 ? target + 1 : target,
  })
}

function childrenOf(sections: readonly Section[], parentId: string): readonly Section[] {
  const parent = find(sections, parentId)
  return parent && isGroup(parent) ? parent.children : []
}

// ── Several sections at once ────────────────────────────────────────────
//
// A statement imported from a PDF is not one section but a run of them (a page
// break, a title, the page's free placement, a text), so the useful unit to
// copy, move or delete is a run. The rules below make a run behave like one
// thing without ever corrupting the tree.

/**
 * The picked sections, outermost only, in the order of the document.
 *
 * A group picked together with what is inside it counts once, as the group:
 * copying both would put its children in twice, and moving both would try to
 * put a section inside itself. The document's order, rather than the order in
 * which they were picked, is what keeps a copied run reading as it did.
 */
export function selectionRoots(
  sections: readonly Section[],
  ids: ReadonlySet<string> | readonly string[],
): Section[] {
  const picked = new Set(ids)
  const roots: Section[] = []

  const visit = (nodes: readonly Section[]): void => {
    for (const node of nodes) {
      if (picked.has(node.id)) roots.push(node)
      else if (isGroup(node)) visit(node.children)
    }
  }

  visit(sections)
  return roots
}

/** The tree without any of the picked sections. */
export function removeMany(
  sections: readonly Section[],
  ids: ReadonlySet<string> | readonly string[],
): Section[] {
  return selectionRoots(sections, ids).reduce<Section[]>(
    (rest, root) => remove(rest, root.id),
    [...sections],
  )
}

/** Where copies of a run go: right after the run, or at the end of the document. */
export type CopyPlace = 'after' | 'end'

/**
 * The tree with a copy of each picked section, and the copies.
 *
 * Copies come in document order and in one piece, after the last of the run
 * (under the parent it has) or at the very end of the document, whatever the
 * originals' parents.
 */
export function duplicateMany(
  sections: readonly Section[],
  ids: ReadonlySet<string> | readonly string[],
  place: CopyPlace,
): { readonly sections: Section[]; readonly copies: Section[] } {
  const roots = selectionRoots(sections, ids)
  const last = roots.at(-1)
  if (!last) return { sections: [...sections], copies: [] }

  const copies = roots.map(duplicateSection)
  if (place === 'end') return { sections: [...sections, ...copies], copies }

  const at = locate(sections, last.id)
  if (!at) return { sections: [...sections], copies: [] }

  const grown = copies.reduce<Section[]>(
    (next, copy, step) => insert(next, copy, { parentId: at.parentId, index: at.index + 1 + step }),
    [...sections],
  )
  return { sections: grown, copies }
}

/** Why a run could not be moved, or null when it may. */
export function refuseMoveMany(
  sections: readonly Section[],
  ids: ReadonlySet<string> | readonly string[],
  to: Position,
): MoveRefusal | null {
  const roots = selectionRoots(sections, ids)
  if (roots.length === 0) return 'no-such-section'

  for (const root of roots) {
    const refusal = refuseMove(sections, root.id, to)
    if (refusal !== null) return refusal
  }
  return null
}

/**
 * The tree with the picked sections moved together, in document order, to a place.
 *
 * The index is read as `move` reads it, against the tree as it stands: the
 * sections of the run that sat before it under the same parent are taken into
 * account, so dragging a run down by one place moves it down by one place.
 * Unchanged when any of it is refused, so a run is never half moved.
 */
export function moveMany(
  sections: readonly Section[],
  ids: ReadonlySet<string> | readonly string[],
  to: Position,
): Section[] {
  if (refuseMoveMany(sections, ids, to) !== null) return [...sections]

  const roots = selectionRoots(sections, ids)
  const before = roots.filter((root) => {
    const at = locate(sections, root.id)
    return at !== null && at.parentId === to.parentId && at.index < to.index
  }).length

  const rest = roots.reduce<Section[]>((left, root) => remove(left, root.id), [...sections])
  const base = to.index - before

  return roots.reduce<Section[]>(
    (next, root, step) => insert(next, root, { parentId: to.parentId, index: base + step }),
    rest,
  )
}

/**
 * The picked sections moved one place together within their parent.
 *
 * Only for a run under one parent, as `nudge` is for one section: a key press
 * must not pull sections out of their group. Picked sections that are not side
 * by side come together as they move.
 */
export function nudgeMany(
  sections: readonly Section[],
  ids: ReadonlySet<string> | readonly string[],
  direction: -1 | 1,
): Section[] {
  const roots = selectionRoots(sections, ids)
  const places = roots.map((root) => locate(sections, root.id))
  const first = places[0]
  if (!first || places.some((at) => at === null || at.parentId !== first.parentId)) {
    return [...sections]
  }

  const indexes = places.map((at) => at?.index ?? 0)
  const siblings = first.parentId === null ? sections : childrenOf(sections, first.parentId)
  const low = Math.min(...indexes)
  const high = Math.max(...indexes)

  if (direction === -1 && low === 0) return [...sections]
  if (direction === 1 && high >= siblings.length - 1) return [...sections]

  return moveMany(sections, ids, {
    parentId: first.parentId,
    index: direction === -1 ? low - 1 : high + 2,
  })
}
